import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Feedback from "ds/components/Feedback";
import FeedbackActions from "ds/components/Feedback/Actions";
import Link from "ds/components/Link";
import useTypedContext from "hooks/useTypedContext";
import FormFieldTextConfirm from "components/FormFields/Confirm";

import ModuleSettingsFormWrapper from "../components/FormWrapper";
import ModuleSettingsPageLayout from "../components/PageLayout";
import { ModuleSettingsContext } from "../Context";
import { DELETE_MODULE } from "../gql";

type FormValues = {
  name: string;
};

const ModuleSettingsDeletion = () => {
  const { module } = useTypedContext(ModuleSettingsContext);
  const navigate = useNavigate();
  const { reportSuccess } = useTypedContext(FlashContext);

  const [deleteModule, { loading }] = useMutation<{ moduleDelete: { id: string } }>(DELETE_MODULE, {
    variables: { moduleId: module.id },
  });

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
    },
    mode: "onBlur",
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const onSubmit = useCallback(
    () =>
      handleSubmit(async () => {
        const { data } = await deleteModule();
        if (data?.moduleDelete?.id) {
          reportSuccess({ message: "Module was successfully deleted" });
          navigate("/modules");
        }
      })(),
    [deleteModule, handleSubmit, navigate, reportSuccess]
  );

  return (
    <ModuleSettingsPageLayout
      title="Module deletion"
      description="You can delete this module, but be aware that this action is irreversible."
    >
      {module.protectFromDeletion && (
        <Feedback type="callout" variant="danger">
          Module is protected from deletion. Go to behavior tab and switch “Protect from deletion”
          toggle off to be able to remove the module.
          <FeedbackActions>
            <Link size="small" href="./../behavior">
              Go to Behavior tab
            </Link>
          </FeedbackActions>
        </Feedback>
      )}
      <ModuleSettingsFormWrapper>
        <FormProvider {...form}>
          <FormFieldTextConfirm
            fieldName="name"
            textToConfirm={module.name}
            disabled={module.protectFromDeletion || loading}
          />
        </FormProvider>
        <Box justify="end" margin="large 0 0">
          <Button
            variant="dangerPrimary"
            onPress={onSubmit}
            disabled={!isValid || !isDirty || loading}
            loading={loading}
          >
            Delete
          </Button>
        </Box>
      </ModuleSettingsFormWrapper>
    </ModuleSettingsPageLayout>
  );
};

export default ModuleSettingsDeletion;
